import React, { useState, useEffect } from 'react';
import { apiClient, API_CONFIG } from '../api/config';
import { Typography, Button, message, Card, Spin, Alert, Tabs } from 'antd';
import AIUpgradePrompt from '../components/AIUpgradePrompt';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import activityTracker from '../user/ActivityTracker';
import { useAILimit } from '../user/AIButtonLimitChecker';
import { logger } from '../utils/logger';
import { useCompany } from '../context/CompanyContext';
import { getFromCacheE, saveExpiryToCache } from '../utils/cacheUtils';
import usePageTracking from '../utils/usePageTracking';

const { Title } = Typography;

function List10Q10K() {
  // GA4 页面追踪
  usePageTracking('10Q/10K財報列表');
  
  const { companyInfo } = useCompany();
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();
  const [companyData, setCompanyData] = useState({
    cik: null,
    isLoading: false,
    error: null,
    filings: [] // 新增存儲過去兩年的 10-K 文件信息
  });
  
  // 新增新聞稿狀態
  const [pressRelease, setPressRelease] = useState(null);
  const [pressLoading, setPressLoading] = useState(false);
  const [pressError, setPressError] = useState(null);
  
  // 新增財報分析狀態
  const [reportAnalysisLoading, setReportAnalysisLoading] = useState(null);
  const [reportAnalysisResult, setReportAnalysisResult] = useState('');
  
  // 分別存儲季報和年報分析結果
  const [quarterlyAnalysis, setQuarterlyAnalysis] = useState('');
  const [annualAnalysis, setAnnualAnalysis] = useState('');
  
  // 新增表格縮放狀態
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  
  // 新增分析結果折疊狀態
  const [isReportAnalysisExpanded, setIsReportAnalysisExpanded] = useState(true);
  
  // 分頁狀態管理
  const [activeTab, setActiveTab] = useState('quarterly');
  
  // 展開縮起狀態管理（共用一個縮放）
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);

  // 當 companyInfo.symbol 變化時自動加載數據
  useEffect(() => {
    if (companyInfo?.symbol) {
      logger.log('list_10q10k Symbol 已变化到:', companyInfo.symbol);
      // 檢查當前內容是否為空或不匹配
      if (companyData.filings.length === 0) {
        handleCompanySelect();
      }
    }
  }, [companyInfo?.symbol]);

  // 記錄頁面訪問
  useEffect(() => {
    if (companyInfo?.symbol) {
      activityTracker.trackPageView(companyInfo.symbol);
    }
  }, [companyInfo?.symbol]);

  // 載入緩存的分析結果
  useEffect(() => {
    if (companyInfo?.symbol) {
      // 檢查是否有季報分析緩存
      const quarterlyCacheKey = `report_analysis_${companyInfo.symbol}_10-Q`;
      const quarterlyCached = getFromCacheE(quarterlyCacheKey);
      if (quarterlyCached) {
        setQuarterlyAnalysis(quarterlyCached);
        logger.log('載入季報分析緩存:', quarterlyCacheKey);
      }
      
      // 檢查是否有年報分析緩存
      const annualCacheKey = `report_analysis_${companyInfo.symbol}_10-K`;
      const annualCached = getFromCacheE(annualCacheKey);
      if (annualCached) {
        setAnnualAnalysis(annualCached);
        logger.log('載入年報分析緩存:', annualCacheKey);
      }
    }
  }, [companyInfo?.symbol]);

  // 處理 SEC JSON 數據
  const processSecData = async (cik) => {
    try {
      const paddedCik = cik.toString().padStart(10, '0');
      const response = await fetch(`https://data.sec.gov/submissions/CIK${paddedCik}.json`);
      const data = await response.json();

      // 獲取最近的文件記錄
      const recentFilings = data.filings.recent;

      // 過濾出過去三年的 10-K、10-Q 和 8-K
      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

      // 先獲取所有財報的日期
      recentFilings.form
        .map((form, index) => ({
          form,
          filingDate: recentFilings.filingDate[index]
        }))
        .filter(filing => 
          (filing.form === '10-K' || filing.form === '10-Q') &&
          new Date(filing.filingDate) >= threeYearsAgo
        )
        .map(filing => {
          // 計算財報日期的前一天
          const reportDate = new Date(filing.filingDate);
          reportDate.setDate(reportDate.getDate() - 1);
          return reportDate.toISOString().split('T')[0];
        });

      // 取得earningdate
      let earningDate = null;
      try {
        const earningRes = await apiClient.get(`${API_CONFIG.endpoints.PRESS.earningdate}/${companyInfo.symbol}`);
        console.log('earningdate API 回傳:', earningRes.data, 'symbol:', companyInfo.symbol);
        if (earningRes.data && earningRes.data.success && earningRes.data.data && earningRes.data.data.length > 0) {
          // 只取日期部分 yyyy-mm-dd
          earningDate = earningRes.data.data[0].earningdate?.slice(0, 10);
        } else {
          console.log('earningdate API 查無資料', companyInfo.symbol);
        }
      } catch (e) {
        logger.error('獲取earningdate失敗', e);
      }

      // 先生成初步 filings
      let filingsRaw = recentFilings.form
        .map((form, index) => {
          const filingDate = recentFilings.filingDate[index];
          let description = '';
          if (form === '10-K') {
            description = '年度財務報告書';
          } else if (form === '10-Q') {
            description = '季度財務報告書';
          } else if (form === '8-K') {
            // 檢查當前8-K文件的上一個文件是否為10-K或10-Q
            const currentIndex = index;
            // 檢查上一個文件（索引減1）
            if (currentIndex > 0) {
              const previousForm = recentFilings.form[currentIndex - 1];

              //logger.log(`8-K日期1: ${filingDate}，上一個文件2: ${previousForm}，上一個文件日期3: ${previousDate}`);

              // 如果上一個文件是10-K或10-Q，則標記為財報新聞稿
              if (previousForm === '10-K' || previousForm === '10-Q') {
                description = '財報新聞稿1';
                //logger.log(`8-K文件 ${filingDate} 被標記為財報新聞稿（上一個文件是${previousForm}）`);
              }
            }
          }
          // 比對earningdate
          if (earningDate && filingDate === earningDate) {
            description = '財報新聞稿2';
          }
          return {
            form,
            accessionNumber: recentFilings.accessionNumber[index],
            primaryDocument: recentFilings.primaryDocument[index],
            filingDate,
            description
          };
        })
        .filter(filing =>
          (filing.form === '10-K' || filing.form === '10-Q' || filing.form === '8-K' || filing.form === '6-K') &&
          new Date(filing.filingDate) >= threeYearsAgo
        );

      // 直接處理所有 filings，不對6-K進行API檢查
      const filingsWith6K = filingsRaw.map(filing => ({
        ...filing,
        url: generateSecUrl(cik, filing.accessionNumber, filing.primaryDocument)
      }));

      // 按照日期排序，最新的在前
      const filteredFilings = filingsWith6K
        .map(filing => ({ ...filing, url: filing.url || generateSecUrl(cik, filing.accessionNumber, filing.primaryDocument) }))
        .sort((a, b) => new Date(b.filingDate) - new Date(a.filingDate));


      setCompanyData(prev => ({
        ...prev,
        filings: filteredFilings
      }));

      return filteredFilings;

    } catch (error) {
      logger.error('處理 SEC 數據時發生錯誤:', error);
      setCompanyData(prev => ({
        ...prev,
        error: '獲取 SEC 文件列表失敗'
      }));
    }
  };

  // 生成 SEC 文件 URL
  const generateSecUrl = (cik, accessionNumber, primaryDocument) => {
    const strippedCik = cik.toString().replace(/^0+/, ''); // 移除開頭的 0
    const strippedAccessionNumber = accessionNumber.replace(/-/g, ''); // 移除 -
    return `https://www.sec.gov/Archives/edgar/data/${strippedCik}/${strippedAccessionNumber}/${primaryDocument}`;
  };

  // 處理新聞稿中的連結 URL
  const processLinkUrl = (linkUrl, baseUrl) => {
    try {
      if (!linkUrl) return baseUrl || '';
      // 如果已經是完整的 URL，直接返回
      if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
        return linkUrl;
      }

      // 如果是相對連結，轉換為絕對連結
      if (linkUrl.startsWith('/')) {
        // 從 baseUrl 提取域名部分
        const urlObj = new URL(baseUrl);
        return `${urlObj.protocol}//${urlObj.host}${linkUrl}`;
      }

      // 如果是相對路徑，需要構建完整 URL
      if (linkUrl.startsWith('./') || !linkUrl.startsWith('/')) {
        const urlObj = new URL(baseUrl);
        const pathParts = urlObj.pathname.split('/');
        pathParts.pop(); // 移除文件名
        const basePath = pathParts.join('/');
        return `${urlObj.protocol}//${urlObj.host}${basePath}/${linkUrl.replace('./', '')}`;
      }

      return linkUrl;
    } catch (error) {
      logger.error('處理連結 URL 時出錯:', error);
      return linkUrl || baseUrl || '';
    }
  };

  // 爬取新聞稿內容
  const scrapePressRelease = async (url) => {
    try {
      logger.log(`調用新聞稿 API: ${API_CONFIG.endpoints.PRESS.SCRAPE}/${companyInfo.symbol}?url=${encodeURIComponent(url)}`);
      const response = await apiClient.get(
        `${API_CONFIG.endpoints.PRESS.SCRAPE}/${companyInfo.symbol}?url=${encodeURIComponent(url)}`
      );
      const result = response.data;
      if (result.success && result.data && result.data.length > 0) {
        logger.log('成功找到新聞稿內容:', result.data);
        return result.data;
      } else {
        logger.log('未找到新聞稿內容:', result.message);
        return [];
      }
    } catch (error) {
      logger.error('爬取新聞稿失敗:', error);
      setPressError(error.response?.data?.message || error.message);
      return [];
    }
  };

  // 檢查並爬取新聞稿
  const checkAndScrapePressRelease = async (filings) => {
    logger.log('開始檢查新聞稿，當前 filings 數量:', filings.length);

    if (!filings || filings.length === 0) {
      logger.log('沒有文件數據，跳過新聞稿檢查');
      return;
    }

    // 設置載入狀態
    setPressLoading(true);
    setPressError(null);

    try {
      // 取得 earningdate
      let earningDate = null;
      try {
        const earningRes = await apiClient.get(`${API_CONFIG.endpoints.PRESS.earningdate}/${companyInfo.symbol}`);
        if (earningRes.data && earningRes.data.success && earningRes.data.data && earningRes.data.data.length > 0) {
          earningDate = earningRes.data.data[0].earningdate?.slice(0, 10);
          logger.log('checkAndScrapePressRelease 取得 earningdate:', earningDate);
        } else {
          logger.log('checkAndScrapePressRelease earningdate API 查無資料', companyInfo.symbol);
        }
      } catch (e) {
        logger.error('checkAndScrapePressRelease 獲取earningdate失敗', e);
      }

      if (!earningDate) {
        logger.log('無 earningdate，跳過新聞稿檢查');
        return;
      }

      // 找到所有與 earningdate 相同的 filing，但只限於8-K或6-K文件
      const matchFilings = filings.filter(filing => 
        filing.filingDate === earningDate && 
        (filing.form === '8-K' || filing.form === '6-K')
      );
      if (!matchFilings || matchFilings.length === 0) {
        logger.log('找不到與 earningdate 相同的 8-K 或 6-K filing，跳過新聞稿檢查');
        return;
      }
      logger.log('找到與 earningdate 相同的 8-K 或 6-K filings:', matchFilings);

      // 依序呼叫 scrapePressRelease 並堆加結果
      let allPressData = [];
      for (const filing of matchFilings) {
        const pressData = await scrapePressRelease(filing.url);
        if (pressData && pressData.length > 0) {
          allPressData = allPressData.concat(pressData);
        }
      }
      if (allPressData.length > 0) {
        setPressRelease(allPressData);
      }
    } catch (error) {
      logger.error('檢查新聞稿時發生錯誤:', error);
      setPressError(error.message);
    } finally {
      setPressLoading(false);
    }
  };

  // 將Markdown轉換為HTML
  const convertMarkdownToHtml = (markdownText) => {
    let html = markdownText;

    // 轉換Markdown table為HTML table - 改進版本，支持更多列數
    const tableRegex = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(\|[^\n]+\|\n?)+)/g;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length < 3) return match; // 至少需要表頭、分隔行和一行數據

      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ddd; font-size: 13px;">';

      lines.forEach((line, index) => {
        if (index === 1) return; // 跳過分隔行 (| --- | --- |)

        const cells = line.split('|').filter(cell => cell.trim() !== '');
        if (cells.length === 0) return;

        const tag = index === 0 ? 'th' : 'td';
        tableHtml += '<tr>';

        cells.forEach((cell, cellIndex) => {
          const content = cell.trim();
          // 根據列數調整樣式
          const isFirstColumn = cellIndex === 0;
          const isLastColumn = cellIndex === cells.length - 1;
          
          let style = '';
          if (index === 0) {
            // 表頭樣式
            style = 'background-color: #f2f2f2; font-weight: bold; padding: 6px 4px; border: 1px solid #ddd; text-align: center; font-size: 12px;';
          } else {
            // 數據行樣式
            style = 'padding: 6px 4px; border: 1px solid #ddd; text-align: left; font-size: 12px;';
            // 第一列通常對齊左邊，其他列根據內容調整
            if (isFirstColumn) {
              style += ' text-align: left;';
            } else {
              style += ' text-align: center;';
            }
          }
          
          tableHtml += `<${tag} style="${style}">${content}</${tag}>`;
        });

        tableHtml += '</tr>';
      });
      tableHtml += '</table>';
      return tableHtml;
    });

    // 轉換其他Markdown格式
    // 粗體
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    // 斜體
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');
    // 標題 - 按順序處理，從最多#到最少#
    html = html.replace(/^#### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; color: #1890ff; font-size: 16px;">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; color: #1890ff; font-size: 18px;">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 12px 0; color: #1890ff; font-size: 20px;">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 16px 0; color: #1890ff; font-size: 22px;">$1</h1>');
    // 列表
    html = html.replace(/^\* (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');
    // 段落
    html = html.replace(/\n\n/g, '</p><p style="margin: 8px 0;">');
    html = html.replace(/^(?!<[h|t|u|o|li])(.*$)/gim, '<p style="margin: 8px 0;">$1</p>');
    // 清理多餘的段落標籤
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p style="margin: 8px 0;"><\/p>/g, '');

    return html;
  };

  // 處理財報分析
  const handleReportAnalysis = async (formType) => {
    if (!companyInfo?.symbol) {
      message.error('請先選擇公司');
      return;
    }

    // AI 使用次數檢查
    const limitCheck = await checkAILimit(`AI_latest_${formType}`);
    if (!limitCheck.canProceed) {
      message.error(limitCheck.reason || 'AI 使用次數已達上限');
      return;
    }

    // 記錄按鈕點擊
    try {
      activityTracker.trackBtnExecution(`AI_latest_${formType}`, companyInfo.symbol, window.location.pathname);
    } catch (e) {}

    // 忽略緩存，強制重新產生並覆蓋緩存
    const cacheKey = `report_analysis_${companyInfo.symbol}_${formType}`;

    setReportAnalysisLoading(formType);
    setReportAnalysisResult('');
    
    // 清空對應的分析結果
    if (formType === '10-Q') {
      setQuarterlyAnalysis('');
    } else if (formType === '10-K') {
      setAnnualAnalysis('');
    }

    try {
      // 建立 WebSocket 連接
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsHost = window.location.hostname === 'localhost'
        ? 'localhost:5080'
        : window.location.host;
      const ws = new WebSocket(`${wsProtocol}://${wsHost}/ws-app`);

      // 設置連接超時
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          logger.error('WebSocket 連接超時');
          message.error('連接超時，請重試');
          setReportAnalysisLoading(null);
          ws.close();
        }
      }, 10000); // 10秒超時

      ws.onopen = () => {
        logger.log('WebSocket 連接已建立');
        clearTimeout(connectionTimeout);

        // 發送分析請求
        ws.send(JSON.stringify({
          type: 'report-analysis',
          symbol: companyInfo.symbol,
          formType: formType
        }));
      };

      let result = '';
      let isCompleted = false;
      let chunkCount = 0;
      let totalReceivedLength = 0;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          logger.log('收到 WebSocket 消息:', data.type);
          
          if (data.error) {
            logger.error('收到錯誤消息:', data.error);
            message.error(data.error);
            setReportAnalysisLoading(null);
            isCompleted = true;
            ws.close();
            return;
          }

          if (data.type === 'report_analysis' && data.content) {
            const contentLength = data.content.length;
            chunkCount++;
            totalReceivedLength += contentLength;
            result += data.content;
            // 實時轉換markdown為HTML
            const htmlResult = convertMarkdownToHtml(result);
            setReportAnalysisResult(htmlResult);
            
            // 根據formType存儲到對應的狀態
            if (formType === '10-Q') {
              setQuarterlyAnalysis(htmlResult);
            } else if (formType === '10-K') {
              setAnnualAnalysis(htmlResult);
            }
            
            // 強制觸發重新渲染
            setTimeout(() => {
              setReportAnalysisResult(prev => {
                if (prev !== htmlResult) {
                  return htmlResult;
                }
                return prev;
              });
            }, 100);
          } else if (data.type === 'complete') {
            logger.log(`前端接收完成: 總計 ${chunkCount} 個片段，總內容長度: ${totalReceivedLength} 字符`);
            // 添加延遲確保內容完全渲染
            setTimeout(() => {
              // 最終確認內容顯示
              const finalHtmlResult = convertMarkdownToHtml(result);
              setReportAnalysisResult(finalHtmlResult);
              
              // 根據formType存儲到對應的狀態
              if (formType === '10-Q') {
                setQuarterlyAnalysis(finalHtmlResult);
              } else if (formType === '10-K') {
                setAnnualAnalysis(finalHtmlResult);
              }
              
              // 保存到緩存
              saveExpiryToCache(cacheKey, finalHtmlResult, 90);
              logger.log(`保存 ${formType} 分析結果到緩存:`, cacheKey);
              
              // 再等待一小段時間確保最終內容顯示
              setTimeout(() => {
                setReportAnalysisLoading(null);
                isCompleted = true;
                ws.close();
                message.success(`${formType} 分析完成`);
              }, 500);
            }, 2000);
          }
        } catch (error) {
          logger.error('解析 WebSocket 消息時出錯:', error);
          // 不要因為解析錯誤就關閉連接，繼續嘗試接收數據
        }
      };

      ws.onerror = (error) => {
        logger.error('WebSocket 錯誤:', error);
        clearTimeout(connectionTimeout);
        
        if (!isCompleted) {
          message.error('連接錯誤，請重試');
          setReportAnalysisLoading(null);
        }
      };

      ws.onclose = (event) => {
        logger.log('WebSocket 連接已關閉，代碼:', event.code, '原因:', event.reason);
        clearTimeout(connectionTimeout);

        // 只有在非正常關閉且未完成時才顯示錯誤
        if (!isCompleted && event.code !== 1000) {
          message.error('連接意外中斷，請重試');
          setReportAnalysisLoading(null);
        }
      };

    } catch (error) {
      logger.error('財報分析時出錯:', error);
      message.error('分析失敗: ' + error.message);
      setReportAnalysisLoading(null);
    }
  };

  // 處理公司選擇
  const handleCompanySelect = async () => {
    const selectedSymbol = companyInfo?.symbol;

    if (!selectedSymbol) {
      setCompanyData(prev => ({
        ...prev,
        error: '請選擇公司',
        filings: []  // 使用 filings 而不是 reports
      }));
      return;
    }

    setCompanyData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // 1. 獲取公司的 CIK
      const cikResponse = await apiClient.get(
        `${API_CONFIG.endpoints.companyCik}/${selectedSymbol}`
      );

      if (cikResponse.data.error) {
        throw new Error(cikResponse.data.error);
      }

      const cik = cikResponse.data.cik;

      // 更新 CIK 並處理 SEC 數據
      setCompanyData(prev => ({
        ...prev,
        cik,
        isLoading: false,
        error: null
      }));

      const filings = await processSecData(cik);
      if (filings && filings.length > 0) {
        await checkAndScrapePressRelease(filings);
      }

    } catch (error) {
      setCompanyData(prev => ({
        ...prev,
        error: error.message,
        isLoading: false,
        filings: []  // 使用 filings 而不是 reports
      }));
    }
  };

  // 判斷是否有6-K
  const has6K = companyData.filings && companyData.filings.some(f => f.form === '6-K');

  return (
    <div className="transcript-search">
      <div className="content-container">
        {/* 第一個區塊：最新財報新聞稿內容和AI財報分析 */}
        <Card 
          title={
            <Title level={4}>
              {companyInfo?.symbol || '未知'} 最新財務報告與分析
            </Title>
          } 
          className="financial-card"
          bodyStyle={{ padding: '12px' }}
          style={{ marginBottom: '16px' }}
        >
          {/* 新聞稿顯示區域 */}
          {pressLoading && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '4px', 
              marginBottom: '16px',
              border: '1px solid #91d5ff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Spin size="small" style={{ marginRight: '8px' }} />
                <span>正在檢查新聞稿內容...</span>
              </div>
            </div>
          )}
          
          {pressError && (
            <Alert
              message="新聞稿檢查失敗"
              description={pressError}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          
          {pressRelease && pressRelease.length > 0 && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f6ffed', 
              borderRadius: '4px', 
              marginBottom: '16px',
              border: '1px solid #b7eb8f'
            }}>
              <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '12px', fontSize: '16px' }}>
                最新財報新聞稿內容 (8-K/6-K)： 
                {pressLoading && (
                  <Spin size="small" style={{ marginLeft: '8px' }} />
                )}
              </div>
              {pressRelease.map((release, index) => (
                <div key={index} style={{ marginBottom: '12px' }}>
                  
                  
                  {/* 顯示內部連結 */}
                  {release.links && release.links.length > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                      
                      {/* 按類型分組顯示連結 */}
                      {(() => {
                        // 智能合併相同URL的連續連結
                        const mergedLinks = [];
                        let currentGroup = null;
                        
                        release.links.forEach((link, index) => {
                          const safeText = link.text || '';
                          if (!currentGroup) {
                            currentGroup = {
                              url: link.url,
                              texts: [safeText],
                              fullText: safeText
                            };
                          } else if (link.url === currentGroup.url) {
                            // 相同URL的連結，合併文本
                            currentGroup.texts.push(safeText);
                            currentGroup.fullText += safeText;
                          } else {
                            // 不同URL，保存當前組並開始新組
                            mergedLinks.push(currentGroup);
                            currentGroup = {
                              url: link.url,
                              texts: [safeText],
                              fullText: safeText
                            };
                          }
                        });
                        
                        // 添加最後一組
                        if (currentGroup) {
                          mergedLinks.push(currentGroup);
                        }
                        
                        // 按類型分組連結
                        const groupedLinks = mergedLinks.reduce((groups, linkGroup) => {
                          // 根據完整文本判斷類型
                          let type = 'other';
                          const fullText = linkGroup.fullText.toLowerCase();
                          
                          if (fullText.includes('press release') || fullText.includes('news release')) {
                            type = 'press';
                          } else if (fullText.includes('exhibit') || fullText.includes('ex-')) {
                            type = 'exhibit';
                          } else if (fullText.includes('form') || fullText.includes('10-') || fullText.includes('8-k')) {
                            type = 'form';
                          } else if (fullText.includes('filing') || fullText.includes('submission')) {
                            type = 'filing';
                          }
                          
                          if (!groups[type]) {
                            groups[type] = [];
                          }
                          groups[type].push(linkGroup);
                          return groups;
                        }, {});
                        
                        return Object.entries(groupedLinks).map(([type, linkGroups]) => (
                          <div key={type} style={{ marginBottom: '12px' }}>
                            {/* 類型標題 */}
                            <div style={{ 
                              fontWeight: 'bold', 
                              color: '#52c41a', 
                              marginBottom: '6px', 
                              fontSize: '13px',
                              borderBottom: '1px solid #d9d9d9',
                              paddingBottom: '2px'
                            }}>
                              {type === 'press' && '📰 新聞稿'}
                              {type === 'exhibit' && '📎 附件文件'}
                              {type === 'form' && '📋 表格文件'}
                              {type === 'filing' && '📄 申報文件'}
                              {type === 'other' && '🔗 其他連結'}
                            </div>
                            
                            {/* 該類型的連結列表 */}
                            {linkGroups.map((linkGroup, linkIndex) => (
                              <div key={linkIndex} style={{ 
                                marginBottom: '4px',
                                padding: '4px 8px',
                                backgroundColor: type === 'press' ? '#f6ffed' : 
                                               type === 'exhibit' ? '#fff7e6' : 
                                               type === 'form' ? '#f0f8ff' : 
                                               type === 'filing' ? '#fff2e8' : '#f9f9f9',
                                borderRadius: '3px',
                                border: `1px solid ${
                                  type === 'press' ? '#b7eb8f' : 
                                  type === 'exhibit' ? '#ffd591' : 
                                  type === 'form' ? '#91d5ff' : 
                                  type === 'filing' ? '#ffbb96' : '#d9d9d9'
                                }`
                              }}>
                                <a 
                                  href={processLinkUrl(linkGroup.url, release.url)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#1890ff', 
                                    textDecoration: 'underline',
                                    fontSize: '13px',
                                    display: 'block'
                                  }}
                                  onClick={(e) => {
                                    // 添加點擊事件處理，確保在新分頁打開
                                    e.preventDefault();
                                    window.open(processLinkUrl(linkGroup.url, release.url), '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  {linkGroup.fullText}
                                </a>
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  
                </div>
              ))}
  {!has6K && <>
  <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '8px', fontSize: '16px' }}>
                最新經營狀況統整報告(10-Q/10-K)
              </div>
              <Button 
                type="primary" 
                size="small"
                onClick={() => {
                  handleReportAnalysis('10-Q');
                  setActiveTab('quarterly');
                }}
                loading={reportAnalysisLoading === '10-Q'}
                style={{ 
                  marginRight: '15px',
                  height: '32px',
                  padding: '4px 12px'
                }}
              >
                最新「季報」分析
              </Button>
              <Button 
                type="primary" 
                size="small"
                onClick={() => {
                  handleReportAnalysis('10-K');
                  setActiveTab('annual');
                }}
                loading={reportAnalysisLoading === '10-K'}
                style={{ 
                  height: '32px',
                  padding: '4px 12px'
                }}
              >
                近期「年報」分析
              </Button>
  </>}
            </div>
          )}
               
          {/* 財報分析分頁區塊 */}
          {(quarterlyAnalysis || annualAnalysis) && (
            <div style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '4px',
                  border: '1px solid #d1ecf1'
                }}
                onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
              >
                <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '14px' }}>
                  財報分析結果：
                </div>
                {isAnalysisExpanded ? <UpOutlined /> : <DownOutlined />}
              </div>
              
              {isAnalysisExpanded && (
                <Tabs 
                  activeKey={activeTab} 
                  onChange={setActiveTab}
                  items={[
                    {
                      key: 'quarterly',
                      label: '季報分析',
                      children: quarterlyAnalysis ? (
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '12px', 
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          maxHeight: '1000px',
                          minHeight: '300px',
                          overflow: 'auto',
                          fontSize: '14px',
                          lineHeight: '1.6',
                          width: '100%'
                        }}
                        dangerouslySetInnerHTML={{ __html: quarterlyAnalysis }}
                        />
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          點擊上方按鈕開始季報分析
                        </div>
                      )
                    },
                    {
                      key: 'annual',
                      label: '年報分析',
                      children: annualAnalysis ? (
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '12px', 
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          maxHeight: '1000px',
                          minHeight: '300px',
                          overflow: 'auto',
                          fontSize: '14px',
                          lineHeight: '1.6',
                          width: '100%'
                        }}
                        dangerouslySetInnerHTML={{ __html: annualAnalysis }}
                        />
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          點擊上方按鈕開始年報分析
                        </div>
                      )
                    }
                  ]}
                />
              )}
            </div>
          )}
        </Card>

        {/* 第二個區塊：文件列表 */}
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4}>
                {companyInfo?.symbol || '未知'} 歷史財務文件列表
              </Title>
              {companyData.filings.length > 0 && (
                <Button
                  type="text"
                  size="small"
                  onClick={() => setIsTableCollapsed(!isTableCollapsed)}
                  style={{ 
                    color: '#1890ff',
                    fontSize: '12px',
                    padding: '4px 8px'
                  }}
                >
                  {isTableCollapsed ? '展開文件列表' : '收起文件列表'}
                </Button>
              )}
            </div>
          } 
          className="financial-card"
          bodyStyle={{ padding: '12px' }}
        >
          {/* 表格摘要（當表格收起時顯示） */}
          {isTableCollapsed && companyData.filings.length > 0 && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              border: '1px solid #d9d9d9'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontWeight: 'bold', color: '#666' }}>
                  文件列表摘要
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setIsTableCollapsed(false)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  展開查看
                </Button>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                共 {companyData.filings.length} 個文件，包含：
                {(() => {
                  const formCounts = companyData.filings.reduce((acc, filing) => {
                    acc[filing.form] = (acc[filing.form] || 0) + 1;
                    return acc;
                  }, {});
                  return Object.entries(formCounts).map(([form, count], index) => (
                    <span key={form}>
                      {index > 0 ? '、' : ''}{form}: {count}個
                    </span>
                  ));
                })()}
              </div>
            </div>
          )}
          
          {companyData.filings.length > 0 && (
            <div 
              className="table-container" 
              style={{ 
                marginTop: '0',
                display: isTableCollapsed ? 'none' : 'block',
                transition: 'all 0.3s ease'
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th className="form-type-col">Form Type</th>
                    <th className="description-col">Description</th>
                    <th className="filing-date-col">Filing Date</th>
                    <th className="document-link-col">Document Link</th>
                  </tr>
                </thead>
                <tbody>
                  {companyData.filings.map((filing, index, array) => {
                    // 檢查是否需要添加空行
                    const prevFiling = index > 0 ? array[index - 1] : null;
                    const needsSpacer = filing.form === '10-K' &&
                                      (!prevFiling || prevFiling.form !== '10-K');

                    return (
                      <React.Fragment key={index}>
                        {needsSpacer && (
                          <tr className="spacer-row">
                            <td colSpan="4"></td>
                          </tr>
                        )}
                        <tr className={filing.form === '10-K' ? 'annual-report-row' : ''}>
                          <td className="form-type-col">{filing.form}</td>
                          <td className="description-col">{filing.description}</td>
                          <td className="filing-date-col">
                            {new Date(filing.filingDate).toLocaleDateString()}
                          </td>
                          <td className="document-link-col">
                            <a
                              href={filing.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {filing.primaryDocument}
                            </a>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <AIUpgradePrompt
        visible={showUpgradePrompt}
        onCancel={() => setShowUpgradePrompt(false)}
        limitInfo={limitInfo}
      />
      <style jsx>{`
        .form-type-col {
          width: 15%;
          min-width: 80px;
        }
        .description-col {
          width: 35%;
          min-width: 120px;
        }
        .filing-date-col {
          width: 15%;
          min-width: 100px;
        }
        .document-link-col {
          width: 35%;
          min-width: 120px;
        }

        @media screen and (max-width: 768px) {
          .form-type-col {
            width: 12%;
            min-width: 60px;
          }
          .description-col {
            width: 38%;
            min-width: 120px;
          }
          .filing-date-col {
            width: 15%;
            min-width: 80px;
          }
          .document-link-col {
            width: 35%;
            min-width: 120px;
          }
          table {
            font-size: 14px;
          }
          th, td {
            padding: 8px 4px;
          }
        }

        @media screen and (max-width: 480px) {
          .form-type-col {
            width: 10%;
            min-width: 50px;
          }
          .description-col {
            width: 30%;
            min-width: 80px;
          }
          .filing-date-col {
            width: 20%;
            min-width: 70px;
          }
          .document-link-col {
            width: 40%;
            min-width: 100px;
          }
          table {
            font-size: 12px;
          }
          th, td {
            padding: 6px 3px;
          }
        }
      `}</style>
    </div>
  );
}

export default List10Q10K; 